import { Theme } from '@mui/material/styles';

/**
 * Severity levels used across the application.
 * Includes both generic levels (error/warning/info) and incident-specific
 * levels (critical/high/medium/low).
 */
export type Severity = 'critical' | 'high' | 'error' | 'warning' | 'medium' | 'info' | 'low' | 'default';

/**
 * Alert status types
 */
export type AlertStatus = 'new' | 'acknowledged' | 'auto_resolved' | 'resolved' | 'dismissed' | 'promoted';

/**
 * Exporter/service status types
 */
export type ServiceStatus = 'up' | 'down' | 'unknown';

/**
 * Get severity color using theme palette (recommended for theme consistency)
 * @param severity - Severity level
 * @param theme - MUI theme object
 * @returns Theme-aware color string
 */
export function getSeverityColorFromTheme(severity: string, theme: Theme): string {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'error':
      return theme.palette.error.main;      // red
    case 'high':
    case 'warning':
      return theme.palette.warning.main;    // dark orange
    case 'medium':
      return theme.palette.warning.light;   // yellow / lighter orange
    case 'low':
    case 'info':
      return theme.palette.info.main;       // light blue
    default:
      return theme.palette.grey[500];
  }
}

/**
 * Get severity color as static CSS value (for non-theme contexts)
 * @param severity - Severity level
 * @returns CSS color string
 */
export function getSeverityColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'error':
      return '#f44336'; // red
    case 'high':
    case 'warning':
      return '#ff9800'; // dark orange
    case 'medium':
      return '#ffb74d'; // lighter orange / yellow
    case 'low':
    case 'info':
      return '#2196f3'; // blue
    default:
      return '#9e9e9e'; // gray
  }
}

/**
 * Get status color using theme palette
 * @param status - Alert status
 * @param theme - MUI theme object
 * @returns Theme-aware color string
 */
export function getStatusColorFromTheme(status: string, theme: Theme): string {
  switch (status.toLowerCase()) {
    case 'new':
      return theme.palette.error.main;
    case 'acknowledged':
      return theme.palette.warning.main;
    case 'auto_resolved':
      return theme.palette.info.main;
    case 'resolved':
    case 'dismissed':
      return theme.palette.success.main;
    case 'promoted':
      return theme.palette.info.main;
    default:
      return theme.palette.grey[500];
  }
}

/**
 * Get status color as static CSS value
 * @param status - Alert status
 * @returns CSS color string
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'new':
      return '#f44336'; // red
    case 'acknowledged':
      return '#ff9800'; // orange
    case 'auto_resolved':
      return '#2196f3'; // blue
    case 'resolved':
    case 'dismissed':
      return '#4caf50'; // green
    case 'promoted':
      return '#2196f3'; // blue
    default:
      return '#9e9e9e'; // gray
  }
}

/**
 * Get service status color using theme palette
 * @param status - Service status (up/down/unknown)
 * @param theme - MUI theme object
 * @returns Theme-aware color string
 */
export function getServiceStatusColorFromTheme(status: ServiceStatus, theme: Theme): string {
  switch (status) {
    case 'up':
      return theme.palette.success.main;
    case 'down':
      return theme.palette.error.main;
    case 'unknown':
    default:
      return theme.palette.grey[500];
  }
}

/**
 * MUI color prop values for Chip/Badge components based on severity
 */
export function getSeverityMuiColor(severity: string): 'error' | 'warning' | 'info' | 'default' {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'error':
      return 'error';
    case 'high':
    case 'warning':
      return 'warning';
    case 'medium':
      return 'warning';
    case 'low':
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

/**
 * MUI color prop values for service status
 */
export function getServiceStatusMuiColor(status: ServiceStatus): 'success' | 'error' | 'default' {
  switch (status) {
    case 'up':
      return 'success';
    case 'down':
      return 'error';
    case 'unknown':
    default:
      return 'default';
  }
}
